import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { useToast } from '../../ui/use-toast';
import { 
  FileSpreadsheet, 
  Download,
  Calendar,
  TrendingUp,
  TrendingDown,
  Users,
  Clock,
  Package,
  BarChart3,
  LineChart,
  PieChart,
  Filter,
  FileDown,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Activity,
  Target,
  Award,
  AlertCircle
} from 'lucide-react';
import { apiService, ApiTeam, ApiUser, ApiWorkEntry, ApiProject, ApiLocation } from '../../../services/api';
import { WorkEntry } from '../../../types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { DatePickerWithRange } from '../../ui/date-range-picker';
import { exportToExcel } from '../../../utils/exportUtils';
import { addDays, format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { ro } from 'date-fns/locale';

interface TeamAnalytics {
  teamId: number;
  teamName: string;
  supervisorName: string;
  totalMembers: number;
  totalHours: number;
  totalDays: number;
  averageHoursPerDay: number;
  productivity: number;
  materialsConsumed: number;
  trend: 'up' | 'down' | 'stable';
}

interface ProductivityMetrics {
  date: string;
  totalWorkers: number;
  activeWorkers: number;
  totalHours: number;
  averageHoursPerWorker: number;
  productivity: number;
}

interface ReportsAnalyticsTabProps {
  monitorId: number;
}

export const ReportsAnalyticsTab: React.FC<ReportsAnalyticsTabProps> = ({
  monitorId
}) => {
  const [teams, setTeams] = useState<ApiTeam[]>([]);
  const [workEntries, setWorkEntries] = useState<ApiWorkEntry[]>([]);
  const [teamAnalytics, setTeamAnalytics] = useState<TeamAnalytics[]>([]);
  const [productivityMetrics, setProductivityMetrics] = useState<ProductivityMetrics[]>([]);
  const [projects, setProjects] = useState<ApiProject[]>([]);
  const [locations, setLocations] = useState<ApiLocation[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: startOfWeek(new Date(), { locale: ro }),
    to: endOfWeek(new Date(), { locale: ro })
  });
  const [reportType, setReportType] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadInitialData();

    // Listen for refresh events
    const handleRefresh = () => loadReportData();
    window.addEventListener('monitor-refresh', handleRefresh);
    
    return () => {
      window.removeEventListener('monitor-refresh', handleRefresh);
    };
  }, [monitorId]);

  useEffect(() => {
    loadReportData();
  }, [dateRange, selectedTeam, selectedProject, selectedLocation]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      
      const [teamsData, projectsData, locationsData] = await Promise.all([
        apiService.getMonitoredTeams(monitorId),
        apiService.getProjects(),
        apiService.getLocations()
      ]);

      setTeams(teamsData);
      setProjects(projectsData);
      setLocations(locationsData);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele inițiale',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadReportData = async () => {
    try {
      setIsLoading(true);
      
      // Format dates for API
      const fromDate = format(dateRange.from, 'yyyy-MM-dd');
      const toDate = format(dateRange.to, 'yyyy-MM-dd');
      
      // Get work entries for date range
      const entriesPromises = [];
      const currentDate = new Date(dateRange.from);
      
      while (currentDate <= dateRange.to) {
        entriesPromises.push(
          apiService.getWorkEntries(format(currentDate, 'yyyy-MM-dd'))
        );
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      const allEntriesResponses = await Promise.all(entriesPromises);
      const allEntries = allEntriesResponses.flatMap(response => 
        Array.isArray(response) ? response : (response.data || [])
      );

      // Filter entries based on monitor's teams
      const teamIds = teams.map(t => t.id);
      const users = await apiService.getAllUsers();
      const teamUserIds = users
        .filter(u => teamIds.includes(u.teamId || 0))
        .map(u => u.id);
      
      let filteredEntries = allEntries.filter(entry => 
        teamUserIds.includes(entry.userId)
      );

      // Apply additional filters
      if (selectedTeam !== 'all') {
        const teamUsers = users
          .filter(u => u.teamId === parseInt(selectedTeam))
          .map(u => u.id);
        filteredEntries = filteredEntries.filter(entry => 
          teamUsers.includes(entry.userId)
        );
      }

      if (selectedProject !== 'all') {
        filteredEntries = filteredEntries.filter(entry => 
          entry.projectId === parseInt(selectedProject)
        );
      }

      if (selectedLocation !== 'all') {
        filteredEntries = filteredEntries.filter(entry => 
          entry.locationId === parseInt(selectedLocation)
        );
      }

      setWorkEntries(filteredEntries);
      
      // Calculate analytics
      calculateTeamAnalytics(filteredEntries, users);
      calculateProductivityMetrics(filteredEntries, users);
      
    } catch (error) {
      console.error('Error loading report data:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele raportului',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const calculateTeamAnalytics = (entries: ApiWorkEntry[], users: ApiUser[]) => {
    const analytics: TeamAnalytics[] = teams.map(team => {
      const teamUsers = users.filter(u => u.teamId === team.id);
      const teamUserIds = teamUsers.map(u => u.id);
      const teamEntries = entries.filter(e => teamUserIds.includes(e.userId));
      
      const totalHours = teamEntries.reduce((sum, entry) => {
        if (!entry.startTime) return sum;
        const start = new Date(entry.startTime);
        const end = entry.endTime ? new Date(entry.endTime) : new Date();
        const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        return sum + hours - (entry.totalBreakMinutes || 0) / 60;
      }, 0);

      const workDays = new Set(
        teamEntries.map(e => format(new Date(e.startTime), 'yyyy-MM-dd'))
      ).size;

      const averageHoursPerDay = workDays > 0 ? totalHours / workDays : 0;
      const expectedHours = workDays * 8 * teamUsers.length;
      const productivity = expectedHours > 0 ? (totalHours / expectedHours) * 100 : 0;

      // Calculate trend (simplified - compare to previous period)
      let trend: 'up' | 'down' | 'stable' = 'stable';
      if (productivity > 85) trend = 'up';
      else if (productivity < 70) trend = 'down';

      const supervisor = users.find(u => u.id === team.supervisorId);

      return {
        teamId: team.id,
        teamName: team.name,
        supervisorName: supervisor?.name || 'N/A',
        totalMembers: teamUsers.length,
        totalHours,
        totalDays: workDays,
        averageHoursPerDay,
        productivity,
        materialsConsumed: 0, // TODO: Implement when materials API is ready
        trend
      };
    });

    setTeamAnalytics(analytics);
  };

  const calculateProductivityMetrics = (entries: ApiWorkEntry[], users: ApiUser[]) => {
    const metrics: ProductivityMetrics[] = [];
    const currentDate = new Date(dateRange.from);
    
    while (currentDate <= dateRange.to) {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const dayEntries = entries.filter(e => 
        format(new Date(e.startTime), 'yyyy-MM-dd') === dateStr
      );

      const activeWorkerIds = new Set(dayEntries.map(e => e.userId));
      const totalHours = dayEntries.reduce((sum, entry) => {
        if (!entry.startTime) return sum;
        const start = new Date(entry.startTime);
        const end = entry.endTime ? new Date(entry.endTime) : new Date();
        const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        return sum + hours - (entry.totalBreakMinutes || 0) / 60;
      }, 0);

      const teamUserIds = users
        .filter(u => teams.some(t => t.id === u.teamId))
        .map(u => u.id);

      metrics.push({
        date: dateStr,
        totalWorkers: teamUserIds.length,
        activeWorkers: activeWorkerIds.size,
        totalHours,
        averageHoursPerWorker: activeWorkerIds.size > 0 ? totalHours / activeWorkerIds.size : 0,
        productivity: teamUserIds.length > 0 ? (activeWorkerIds.size / teamUserIds.length) * 100 : 0
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    setProductivityMetrics(metrics);
  };

  const handleQuickDateRange = (type: 'today' | 'week' | 'month') => {
    const today = new Date();
    let from: Date, to: Date;

    switch (type) {
      case 'today':
        from = to = today;
        break;
      case 'week':
        from = startOfWeek(today, { locale: ro });
        to = endOfWeek(today, { locale: ro });
        break;
      case 'month':
        from = startOfMonth(today);
        to = endOfMonth(today);
        break;
    }

    setDateRange({ from, to });
  };

  const handleGenerateReport = async (type: 'team' | 'productivity' | 'full') => {
    try {
      setIsGenerating(true);

      const filename = `raport-monitor-${type}-${format(dateRange.from, 'yyyy-MM-dd')}-${format(dateRange.to, 'yyyy-MM-dd')}.xlsx`;
      
      // Convert ApiWorkEntry to WorkEntry format
      const workEntryData: WorkEntry[] = workEntries.map(entry => ({
        id: entry.id,
        userId: entry.userId,
        userName: entry.userName || 'N/A',
        projectId: entry.projectId,
        projectName: entry.projectName || 'N/A',
        locationId: entry.locationId,
        locationName: entry.locationName || 'N/A',
        startTime: entry.startTime,
        endTime: entry.endTime,
        breakMinutes: entry.totalBreakMinutes || 0,
        comments: entry.comment || '',
        workDescription: entry.workDescription || '',
        voiceRecording: '',
        date: format(new Date(entry.startTime), 'yyyy-MM-dd'),
        isActive: entry.isActive
      }));

      await exportToExcel(workEntryData, filename);
      
      toast({
        title: 'Export realizat',
        description: `Raportul ${type} a fost generat cu succes`,
      });
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut genera raportul',
        variant: 'destructive'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      default:
        return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  // Calculate summary metrics
  const totalTeams = teamAnalytics.length;
  const totalWorkers = teamAnalytics.reduce((sum, t) => sum + t.totalMembers, 0);
  const totalHours = teamAnalytics.reduce((sum, t) => sum + t.totalHours, 0);
  const averageProductivity = teamAnalytics.length > 0 
    ? teamAnalytics.reduce((sum, t) => sum + t.productivity, 0) / teamAnalytics.length 
    : 0;

  return (
    <div className="space-y-6">
      {/* Filters Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtre Rapoarte
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range Picker */}
            <div className="space-y-2">
              <Label>Perioada</Label>
              <DatePickerWithRange
                date={dateRange}
                setDate={setDateRange}
              />
              <div className="flex gap-1 mt-2">
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => handleQuickDateRange('today')}
                >
                  Azi
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => handleQuickDateRange('week')}
                >
                  Săptămână
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => handleQuickDateRange('month')}
                >
                  Lună
                </Button>
              </div>
            </div>

            {/* Team Filter */}
            <div className="space-y-2">
              <Label>Echipă</Label>
              <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                <SelectTrigger>
                  <SelectValue placeholder="Toate echipele" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate echipele</SelectItem>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.id.toString()}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Project Filter */}
            <div className="space-y-2">
              <Label>Proiect</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger>
                  <SelectValue placeholder="Toate proiectele" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate proiectele</SelectItem>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id.toString()}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location Filter */}
            <div className="space-y-2">
              <Label>Locație</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger>
                  <SelectValue placeholder="Toate locațiile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate locațiile</SelectItem>
                  {locations.map(location => (
                    <SelectItem key={location.id} value={location.id.toString()}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Users className="h-8 w-8 text-blue-500 mb-2" />
              <p className="text-sm text-gray-600">Total Echipe</p>
              <p className="text-2xl font-bold">{totalTeams}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Activity className="h-8 w-8 text-green-500 mb-2" />
              <p className="text-sm text-gray-600">Total Muncitori</p>
              <p className="text-2xl font-bold">{totalWorkers}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Clock className="h-8 w-8 text-purple-500 mb-2" />
              <p className="text-sm text-gray-600">Total Ore</p>
              <p className="text-2xl font-bold">{totalHours.toFixed(0)}h</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Target className="h-8 w-8 text-orange-500 mb-2" />
              <p className="text-sm text-gray-600">Productivitate</p>
              <p className="text-2xl font-bold">{averageProductivity.toFixed(0)}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Analytics Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Analiza Echipelor
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleGenerateReport('team')}
              disabled={isGenerating}
              className="flex items-center gap-2"
            >
              {isGenerating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Export Echipe
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-gray-700">Echipă</th>
                  <th className="text-left p-3 font-medium text-gray-700">Supervisor</th>
                  <th className="text-center p-3 font-medium text-gray-700">Membri</th>
                  <th className="text-center p-3 font-medium text-gray-700">Ore Totale</th>
                  <th className="text-center p-3 font-medium text-gray-700">Zile Lucrate</th>
                  <th className="text-center p-3 font-medium text-gray-700">Media/Zi</th>
                  <th className="text-center p-3 font-medium text-gray-700">Productivitate</th>
                  <th className="text-center p-3 font-medium text-gray-700">Trend</th>
                </tr>
              </thead>
              <tbody>
                {teamAnalytics.map((team) => (
                  <tr key={team.teamId} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium">{team.teamName}</td>
                    <td className="p-3">{team.supervisorName}</td>
                    <td className="p-3 text-center">{team.totalMembers}</td>
                    <td className="p-3 text-center">{team.totalHours.toFixed(1)}h</td>
                    <td className="p-3 text-center">{team.totalDays}</td>
                    <td className="p-3 text-center">{team.averageHoursPerDay.toFixed(1)}h</td>
                    <td className="p-3 text-center">
                      <Badge 
                        variant={
                          team.productivity >= 85 ? 'success' : 
                          team.productivity >= 70 ? 'warning' : 
                          'destructive'
                        }
                      >
                        {team.productivity.toFixed(0)}%
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      {getTrendIcon(team.trend)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Productivity Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <LineChart className="h-5 w-5" />
              Evoluție Productivitate
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleGenerateReport('productivity')}
              disabled={isGenerating}
              className="flex items-center gap-2"
            >
              {isGenerating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Export Productivitate
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {productivityMetrics.map((metric) => (
              <div key={metric.date} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <Calendar className="h-4 w-4 text-gray-600" />
                  <span className="font-medium">
                    {format(new Date(metric.date), 'EEEE, d MMMM', { locale: ro })}
                  </span>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-xs text-gray-600">Activi/Total</p>
                    <p className="font-medium">{metric.activeWorkers}/{metric.totalWorkers}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600">Ore</p>
                    <p className="font-medium">{metric.totalHours.toFixed(1)}h</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600">Media/Muncitor</p>
                    <p className="font-medium">{metric.averageHoursPerWorker.toFixed(1)}h</p>
                  </div>
                  <Badge 
                    variant={
                      metric.productivity >= 80 ? 'success' : 
                      metric.productivity >= 60 ? 'warning' : 
                      'destructive'
                    }
                  >
                    {metric.productivity.toFixed(0)}%
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Acțiuni Rapide
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2"
              onClick={() => handleGenerateReport('full')}
              disabled={isGenerating}
            >
              <FileSpreadsheet className="h-8 w-8 text-blue-500" />
              <span>Raport Complet</span>
            </Button>

            <Button
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2"
              onClick={() => {
                const bestTeam = teamAnalytics.reduce((best, current) => 
                  current.productivity > best.productivity ? current : best
                , teamAnalytics[0]);
                
                if (bestTeam) {
                  toast({
                    title: 'Cea mai productivă echipă',
                    description: `${bestTeam.teamName} - ${bestTeam.productivity.toFixed(0)}% productivitate`,
                  });
                }
              }}
            >
              <Award className="h-8 w-8 text-gold-500" />
              <span>Top Performeri</span>
            </Button>

            <Button
              variant="outline"
              className="h-24 flex flex-col items-center justify-center gap-2"
              onClick={() => {
                const needsAttention = teamAnalytics.filter(t => t.productivity < 70);
                if (needsAttention.length > 0) {
                  toast({
                    title: 'Echipe care necesită atenție',
                    description: `${needsAttention.length} echipe au productivitate sub 70%`,
                    variant: 'destructive'
                  });
                } else {
                  toast({
                    title: 'Performanță bună',
                    description: 'Toate echipele au productivitate peste 70%',
                  });
                }
              }}
            >
              <AlertCircle className="h-8 w-8 text-orange-500" />
              <span>Alertă Performanță</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Insights Alert */}
      {averageProductivity < 70 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Productivitatea medie ({averageProductivity.toFixed(0)}%) este sub nivelul optim. 
            Verificați echipele cu performanță scăzută și discutați cu supervizori pentru îmbunătățiri.
          </AlertDescription>
        </Alert>
      )}

      {averageProductivity >= 85 && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Productivitate excelentă! Continuați să mențineți aceste standarde înalte.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};